import React, { useEffect, useState } from "react";
import { Card, Select, Input, Button, Typography, message, Space, Flex } from "antd";
import { QrcodeOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { fetchManufacturers, fetchCuttingLogic } from "../../../Purchase/CuttingLogic/cuttingLogicThunk";
import { setFormField, addPackingItem } from "../packingSlice";

const { Text, Title } = Typography;

// When `onAdd` is provided, the parsed item is handed to the caller and the
// manufacturer/QR fields are kept in local state instead of the shared
// tatmeenPacking Redux slice — used by the Edit drawer so it doesn't
// interfere with the separate "new packing" entry form's state.
const PackingQRParser = ({ onAdd }) => {
    const dispatch = useDispatch();

    const { manufacturers, logicList } = useSelector((s) => s.cuttingLogic);
    const reduxParser = useSelector((s) => s.tatmeenPacking);

    const [localManufacturer, setLocalManufacturer] = useState(null);
    const [localQrCode, setLocalQrCode] = useState("");

    const parserManufacturer = onAdd ? localManufacturer : reduxParser.parserManufacturer;
    const qrCode = onAdd ? localQrCode : reduxParser.qrCode;

    const [loading, setLoading] = useState(false);

    useEffect(() => {
        dispatch(fetchManufacturers());
        dispatch(fetchCuttingLogic());
    }, [dispatch]);

    const setManufacturer = (value) => {
        if (onAdd) {
            setLocalManufacturer(value);
        } else {
            dispatch(setFormField({ field: "parserManufacturer", value }));
        }
    };

    const setQrCode = (value) => {
        if (onAdd) {
            setLocalQrCode(value);
        } else {
            dispatch(setFormField({ field: "qrCode", value }));
        }
    };

    const parseAndAdd = () => {
        if (!parserManufacturer) {
            message.warning("Select Manufacturer");
            return;
        }

        if (!qrCode) {
            message.warning("Enter QR Code");
            return;
        }

        const logic = logicList.find(
            (x) => String(x.manufacture_id) === String(parserManufacturer)
        );

        if (!logic) {
            message.warning("Cutting logic not configured for this manufacturer");
            return;
        }

        setLoading(true);

        try {
            const getAI = (start) => qrCode.substring(start - 3, start - 1);
            const extract = (from, to) => qrCode.substring(from - 1, to);

            if (getAI(logic.gtin_from) !== "01") {
                message.error("GTIN AI mismatch");
                return;
            }
            if (getAI(logic.batch_from) !== "10") {
                message.error("Batch AI mismatch");
                return;
            }
            if (getAI(logic.expiry_from) !== "17") {
                message.error("Expiry AI mismatch");
                return;
            }
            if (getAI(logic.serial_from) !== "21") {
                message.error("Serial AI mismatch");
                return;
            }

            const gtin = extract(logic.gtin_from, logic.gtin_to);
            const batch_name = extract(logic.batch_from, logic.batch_to);
            const batch_expiry = extract(logic.expiry_from, logic.expiry_to);
            const serial_number = extract(logic.serial_from, logic.serial_to);

            const manufacturer = manufacturers.find(
                (m) => String(m.id) === String(parserManufacturer)
            );

            const item = {
                manufacture_id: parserManufacturer,
                manufacturer_name: manufacturer?.name || "",
                gtin,
                serial_number,
                batch_name,
                batch_expiry,
                raw_qr_code: qrCode,
            };

            if (onAdd) {
                onAdd(item);
            } else {
                dispatch(addPackingItem(item));
            }

            setQrCode("");

            message.success("Product added");
        } catch {
            message.error("Parse failed");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Card
            bordered={false}
            style={{
                borderRadius: 12,
                boxShadow: "0 2px 12px rgba(0,0,0,.08)",
            }}
            title={
                <Flex align="center" gap={10}>
                    <QrcodeOutlined style={{ color: "#1677ff", fontSize: 22 }} />
                    <Title level={5} style={{ margin: 0 }}>
                        Scan Product
                    </Title>
                </Flex>
            }
        >
            <Space direction="vertical" style={{ width: "100%" }} size={12}>
                <div>
                    <Text strong>Manufacturer</Text>
                    <Select
                        showSearch
                        style={{ width: "100%", marginTop: 5 }}
                        placeholder="Select Manufacturer"
                        value={parserManufacturer}
                        optionFilterProp="label"
                        options={manufacturers.map((m) => ({ label: m.name, value: m.id }))}
                        onChange={setManufacturer}
                    />
                </div>

                <div>
                    <Text strong>QR / Barcode</Text>
                    <Input.TextArea
                        rows={3}
                        style={{ marginTop: 5 }}
                        value={qrCode}
                        disabled={!parserManufacturer}
                        placeholder="Scan or paste QR code"
                        onChange={(e) => setQrCode(e.target.value)}
                        onPressEnter={(e) => {
                            e.preventDefault();
                            parseAndAdd();
                        }}
                    />
                </div>

                <Button
                    type="primary"
                    icon={<QrcodeOutlined />}
                    loading={loading}
                    onClick={parseAndAdd}
                    block
                >
                    Parse &amp; Add
                </Button>
            </Space>
        </Card>
    );
};

export default PackingQRParser;
